import React, { useEffect, useRef, useState } from "react";
import { Button } from "../components/AdminSharedComponents";
import { Image, FileText, FileSpreadsheet, File, Download, Edit, X, Folder, Calendar } from "lucide-react";

const ReportModal = ({ isOpen, onClose, report, project }) => {
  const modalRef = useRef(null);
  const [attachmentError, setAttachmentError] = useState({});
  const [userRole, setUserRole] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Fetch user role
  const fetchUserRole = async () => {
    try {
      // Get role from localStorage (same as main component approach)
      const user = JSON.parse(localStorage.getItem("user"));
      const role = user?.role || 'admin';
      setUserRole(role);
    } catch (error) {
      console.error("Error fetching user role:", error);
      setUserRole('admin'); // Default fallback
    }
  };

  // Role-based access control
  const isSupervisor = userRole === "supervisor";
  const hasEditAccess = !isSupervisor;

  // Update isMobile state when window is resized
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch user role on component mount
  useEffect(() => {
    fetchUserRole();
  }, []);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "auto";
    };
  }, [isOpen, onClose]);

  const handleDownload = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/reports/${report.id}/download`
      );
      if (!response.ok) throw new Error("Gagal mengunduh laporan");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${report.title.replace(/\s+/g, "_")}_full_report.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      alert("Terjadi kesalahan saat mengunduh laporan lengkap.");
      console.error(error);
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType.startsWith("image/")) {
      return <Image className="h-5 w-5" />;
    } else if (fileType === "application/pdf") {
      return <FileText className="h-5 w-5" color="#E53935" />;
    } else if (
      fileType.includes("excel") ||
      fileType.includes("spreadsheet") ||
      fileType.includes("xls")
    ) {
      return <FileSpreadsheet className="h-5 w-5" color="#1D6F42" />;
    } else {
      return <File className="h-5 w-5" />;
    }
  };

  // Format file size
  const formatFileSize = (size) => {
    if (size < 1024) {
      return size + " B";
    } else if (size < 1024 * 1024) {
      return (size / 1024).toFixed(2) + " KB";
    } else {
      return (size / (1024 * 1024)).toFixed(2) + " MB";
    }
  };

  // Get consistent attachment URL for both display and download
  const getAttachmentUrl = (attachment) => {
    const baseUrl = process.env.REACT_APP_API_URL || "";
    return `${baseUrl}/uploads/reports/${attachment.filename}`;
  };

  // Handle attachment download with error handling
  const handleAttachmentDownload = (attachment) => {
    try {
      const attachmentUrl = getAttachmentUrl(attachment);

      // Create a fetch request to check if file exists
      fetch(attachmentUrl, { method: "HEAD" })
        .then((response) => {
          if (response.ok) {
            // File exists, proceed with download
            const a = document.createElement("a");
            a.href = attachmentUrl;
            a.download = attachment.originalname || "download";
            a.setAttribute("target", "_blank");
            a.setAttribute("rel", "noopener noreferrer");
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          } else {
            console.error("File not found:", attachmentUrl);
            alert(
              `Error: Unable to find the file "${attachment.originalname}"`
            );
          }
        })
        .catch((error) => {
          console.error("Download error:", error);
          alert(`Error downloading the file: ${error.message}`);
        });
    } catch (error) {
      console.error("Download error:", error);
      alert(`Error preparing download: ${error.message}`);
    }
  };

  // Handle image loading error
  const handleImageError = (id) => {
    setAttachmentError((prev) => ({ ...prev, [id]: true }));
    console.error(`Failed to load image for attachment ID: ${id}`);
  };

  if (!isOpen || !report) return null;

  // Categorize attachments by type
  const imageAttachments = report.attachments
    ? report.attachments.filter((attachment) =>
        attachment.mimetype.startsWith("image/")
      )
    : [];
  const pdfAttachments = report.attachments
    ? report.attachments.filter(
        (attachment) => attachment.mimetype === "application/pdf"
      )
    : [];
  const otherAttachments = report.attachments
    ? report.attachments.filter(
        (attachment) =>
          !attachment.mimetype.startsWith("image/") &&
          attachment.mimetype !== "application/pdf"
      )
    : [];

  // Check if report has attachments
  const hasAttachments = report.attachments && report.attachments.length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
      <div
        ref={modalRef}
        className={`bg-white flex flex-col overflow-hidden ${
          isMobile
            ? "fixed inset-0 w-full h-full"
            : "rounded-lg shadow-xl w-full max-w-6xl h-[90vh] mx-4"
        }`}
      >
        {/* Header with actions */}
        <div className="p-4 sm:p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl sm:text-2xl font-semibold text-primary-dark truncate pr-2">
            {report.title}
          </h2>

          {/* Desktop actions */}
          {!isMobile && (
            <div className="flex gap-2">
              <Button
                variant="primary"
                className="flex items-center gap-1 !py-1.5 text-sm"
                onClick={handleDownload}
              >
                <Download className="h-4 w-4" />
                Download ZIP
              </Button>

              {/* Only show Edit button for non-supervisor roles */}
              {hasEditAccess && (
                <Button
                  variant="secondary"
                  className="flex items-center gap-1 !py-1.5 text-sm"
                  onClick={() =>
                    (window.location.href = `/admin/reports/edit/${report.id}`)
                  }
                >
                  <Edit className="h-4 w-4" />
                  Edit
                </Button>
              )}
              <Button
                variant="text"
                className="!py-1.5 text-sm"
                onClick={onClose}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          )}

          {/* Mobile close button */}
          {isMobile && (
            <button
              className="p-2 rounded-full hover:bg-gray-100"
              onClick={onClose}
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
          )}
        </div>

        {/* Report metadata */}
        <div className="bg-gray-50 p-4 border-b border-gray-200">
          <div className="flex flex-wrap gap-3">
            {project && (
              <div className="text-sm flex items-center gap-1 bg-primary bg-opacity-10 text-primary px-2 py-1 rounded">
                <Folder className="h-4 w-4" />
                {project.name}
              </div>
            )}
            {project?.status && (
              <div
                className={`text-sm px-2 py-1 rounded ${
                  project.status === "Completed"
                    ? "bg-green-100 text-green-800"
                    : project.status === "In Progress"
                    ? "bg-blue-100 text-blue-800"
                    : "bg-yellow-100 text-yellow-800"
                }`}
              >
                {project.status}
              </div>
            )}
            <div className="text-sm text-gray-500 flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {new Date(report.created_at).toLocaleDateString()}
            </div>
          </div>
        </div>

        {/* Report content */}
        <div className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="mb-6">
            <h3 className="font-medium text-gray-700 mb-2">Description</h3>
            <p className="text-gray-600 whitespace-pre-line">
              {report.description}
            </p>
          </div>

          {/* Report content section */}
          {report.content && (
            <div className="mb-6">
              <h3 className="font-medium text-gray-700 mb-2">Report Content</h3>
              <div className="prose max-w-none">{report.content}</div>
            </div>
          )}

          {/* Integrated Attachments with Previews */}
          {hasAttachments && (
            <div className="mb-6">
              <h3 className="font-medium text-gray-700 mb-2">Attachments</h3>

              {/* Other file attachments */}
              {otherAttachments.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  {otherAttachments.map((attachment) => (
                    <div
                      key={attachment.id}
                      className="flex items-center justify-between p-2 border-b last:border-b-0"
                    >
                      <div className="flex items-center gap-2">
                        {getFileIcon(attachment.mimetype)}
                        <div>
                          <p className="font-medium text-sm">
                            {attachment.originalname}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatFileSize(attachment.size)}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleAttachmentDownload(attachment)}
                        className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm"
                      >
                        <Download className="h-4 w-4" />
                        Download
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Image Attachments */}
              {imageAttachments.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  {imageAttachments.map((attachment) => (
                    <div
                      key={`attachment-${attachment.id}`}
                      className="border rounded-lg overflow-hidden"
                    >
                      <div className="bg-gray-50 p-2 border-b flex justify-between items-center">
                        <p className="font-medium text-sm truncate">
                          {attachment.originalname}
                        </p>
                        <button
                          onClick={() => handleAttachmentDownload(attachment)}
                          className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm"
                        >
                          <Download className="h-4 w-4" />
                          Download
                        </button>
                      </div>
                      {!attachmentError[attachment.id] ? (
                        <img
                          src={getAttachmentUrl(attachment)}
                          alt={attachment.originalname}
                          className="w-full h-auto max-h-64 object-contain p-2"
                          onError={() => handleImageError(attachment.id)}
                        />
                      ) : (
                        <div className="h-32 flex items-center justify-center text-red-500">
                          <p>Unable to load image</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* PDF Attachments */}
              {pdfAttachments.length > 0 && (
                <div className="grid grid-cols-1 gap-4">
                  {pdfAttachments.map((attachment) => (
                    <div
                      key={`pdf-preview-${attachment.id}`}
                      className="border rounded-lg overflow-hidden"
                    >
                      <div className="bg-gray-50 p-2 border-b flex justify-between items-center">
                        <p className="font-medium">{attachment.originalname}</p>
                        <button
                          onClick={() => handleAttachmentDownload(attachment)}
                          className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm"
                        >
                          <Download className="h-4 w-4" />
                          Download
                        </button>
                      </div>
                      <iframe
                        src={`${getAttachmentUrl(attachment)}#toolbar=0`}
                        className="w-full h-96"
                        title={`PDF Preview: ${attachment.originalname}`}
                        onError={() => handleImageError(attachment.id)}
                      ></iframe>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex justify-between">
          {/* Mobile action buttons */}
          {isMobile && (
            <div className="flex space-x-2">
              <Button
                variant="primary"
                className="flex items-center gap-1 !py-2"
                onClick={handleDownload}
              >
                <Download className="h-4 w-4" />
                Download
              </Button>
              
              {/* Only show Edit button for non-supervisor roles on mobile */}
              {hasEditAccess && (
                <Button
                  variant="secondary"
                  className="flex items-center gap-1 !py-2"
                  onClick={() =>
                    (window.location.href = `/admin/reports/edit/${report.id}`)
                  }
                >
                  <Edit className="h-4 w-4" />
                  Edit
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportModal;